import { BrandMark } from '@pa/ui'
import { ArrowLeft, X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'

/**
 * The entry panel (UserFlows §2.3: P-01 to P-06, P-14 to P-17): header "Book an appointment" with the clinic name,
 * footer "Powered by Clinic Scheduler". Ported from the mockup's `site/Widget.tsx` `EntryPanel`; on the direct link it
 * sits in the page's `.wg-static` card (Frontend §4.5). In the embed (task 08, `variant="embed"`) it fills the panel
 * iframe, has the panel X (`onClose`), and `rootRef` is the element whose height the host sizes the iframe to.
 */
export function EntryPanel({
  clinicName,
  children,
  variant = 'static',
  onClose,
  rootRef,
}: {
  clinicName: string | undefined
  children: ReactNode
  variant?: 'static' | 'embed'
  onClose?: () => void
  rootRef?: (el: HTMLElement | null) => void
}) {
  return (
    <div className={`wg wg-${variant}`} ref={rootRef}>
      <section className="wg-panel" aria-label="Book an appointment">
        <div className="wg-head">
          <span className="pt-logo">
            <BrandMark />
          </span>
          <div className="grow">
            <b>Book an appointment</b>
            {clinicName !== undefined && <span>{clinicName}</span>}
          </div>
          {onClose && (
            <button type="button" className="wg-x" aria-label="Close" onClick={onClose}>
              <X aria-hidden />
            </button>
          )}
        </div>
        <div className="wg-body">{children}</div>
        <div className="wg-foot">Powered by Clinic Scheduler</div>
      </section>
    </div>
  )
}

/** The mockup's `BackButton` (`back-link wg-back`). */
export function BackButton({ onClick, label = 'Back' }: { onClick: () => void; label?: string }) {
  return (
    <button type="button" className="back-link wg-back" onClick={onClick}>
      <ArrowLeft aria-hidden /> {label}
    </button>
  )
}

/**
 * A screen's `<h1>`, focused when the screen appears (Frontend §4.11: every terminal state has an `<h1>`, and focus
 * moves to it).
 */
export function ScreenTitle({ children, className = 'wg-title' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [])
  return (
    <h1 ref={ref} className={className} tabIndex={-1}>
      {children}
    </h1>
  )
}

/** A centred state icon (mockup `wg-state` / `state-icon tone-*`). */
export function StateIcon({ tone, children }: { tone: 'warn' | 'error' | 'info' | 'neutral'; children: ReactNode }) {
  return (
    <div className="wg-state">
      <span className={`state-icon tone-${tone}`} aria-hidden>
        {children}
      </span>
    </div>
  )
}
